<template>
  <ActionPanelShell title="New task" :subtitle="subtitle" @close="close">
    <div class="sp" data-test="new-task-panel">
      <section class="sp-sec nt-compose" data-test="new-task-compose">
        <p class="sp-sub nt-hint">
          Type the task. Use <b>@</b> to assign it, <b>#</b> to add another patient, and plain-language dates like “by Friday”.
          {{ firstName }} is already attached.
        </p>
        <!-- The Tasks page's own composer (chips: @, dates, repeats, the mic), patient pre-seeded the way
             CreateTaskPanel seeds it. The footer button calls saveTodo(); Enter does the same. -->
        <TaskComposer
          ref="composer"
          variant="inline"
          :seed-patient="patient"
          :enforce-patient="true"
          :available-tags="availableTags"
          :dropdowns-in-body="false"
          autofocus
          @saved="onSaved"
        />
      </section>

      <!-- The patient's open tasks, so nothing gets doubled up. Read from the slot the page already
           listens to (tasks/tasksTaggedWithPatient) — this panel adds no listener of its own. -->
      <section v-if="openTasks.length" class="sp-sec" data-test="new-task-open">
        <div class="sp-head">
          <span class="sp-h">Already open for {{ firstName }}</span>
          <span class="sp-sub nt-count" data-test="new-task-open-count">{{ openTasks.length }}</span>
        </div>
        <ul class="nt-open">
          <li v-for="t in openTasks" :key="t.id" class="nt-open__row" data-test="new-task-open-row">
            <span class="nt-open__ring" aria-hidden="true"></span>
            <span class="nt-open__body">
              <span class="nt-open__text">{{ previewText(t) }}</span>
              <span class="nt-open__meta">
                <span><i class="fa fa-user-o" aria-hidden="true"></i> {{ assigneeNames(t) }}</span>
                <span><i class="fa fa-calendar-o" aria-hidden="true"></i> {{ formatDue(t) }}</span>
              </span>
            </span>
          </li>
        </ul>
      </section>
    </div>

    <template #footer>
      <span class="ap-foot-note" data-test="new-task-foot-note">Enter adds it</span>
      <button
        type="button"
        class="nt-foot-btn ap-foot-main"
        :disabled="!canAdd"
        data-test="new-task-add"
        @click="add"
      >{{ saving ? 'Adding…' : 'Add task' }}</button>
    </template>
  </ActionPanelShell>
</template>

<script>
/**
 * New task — the redesigned panel (design "Patient Action Panels" 2f), opened by
 * PatientPanelActionHost.run('task') through openActionPanel() (480px, 'ap-slideout'); it replaces the
 * 800px CreateTaskPanel on the new Patient Profile page and the Schedule View popover.
 *
 * The Tasks page's own TaskComposer, `variant="inline"` (@ to assign, plain-language dates, the mic),
 * seeded with THIS patient exactly as CreateTaskPanel seeds it (seed-patient + enforce-patient, so
 * the saved task is tagged even if the chip is deleted). Saving is the composer's own saveTodo() —
 * the footer's "Add task" button calls it, as Enter does.
 *
 * Below the composer: the patient's open tasks, from the `tasks/tasksTaggedWithPatient` slot, filtered
 * by `taggedPatients` exactly as PatientTasksCard does. That slot is SHARED app-wide (another page's
 * stale listener can overwrite it with someone else's tasks), hence the filter. This panel does NOT
 * subscribe: the Patient Profile page (PatientTasksCard) and the Schedule View panel (the patientPanel
 * store) already keep it live. The section is hidden when there are none.
 *
 * Tags are edited in the task drawer, not here — the inline composer has no tag box.
 *
 * ⛔ No mixin.js, and no prop named `patNum`, `siteID`, `loading` or `allSettings`.
 */
import moment from 'moment-timezone';
import TaskComposer from '@/components/TaskComposer/TaskComposer.vue';
import { stripMentions, hasTaskText } from '@/utils/taskPatients';
import { dateFormatFor } from '@/utils/taskDraft';
import ActionPanelShell from './ActionPanelShell.vue';

// How a task's dueAt is stored (PatientTasksCard DUE_FORMAT).
const DUE_FORMAT = 'MM/DD/YYYY h:mm:ss a';

export default {
  name: 'NewTaskPanel',

  components: { ActionPanelShell, TaskComposer },

  props: {
    /** { PatNum, LName, FName } — what CreateTaskPanel takes. */
    patient: { type: Object, required: true },
  },

  data() {
    // The composer, once mounted. A $ref is not reactive; held in data it lets `canAdd` follow the
    // composer's own draft (a Vue instance is never made reactive itself, only read through).
    return { composer: null };
  },

  computed: {
    firstName() {
      return (this.patient && this.patient.FName) || 'this patient';
    },
    subtitle() {
      const p = this.patient || {};
      const name = [p.FName, p.LName].filter(Boolean).join(' ').trim();
      return name ? `For ${name}` : '';
    },
    availableTags() {
      return (this.$store.state.tasks && this.$store.state.tasks.siteTaskTags) || [];
    },
    employees() {
      return (this.$store.state.customer && this.$store.state.customer.employees) || [];
    },
    officeDateFormat() {
      const rows = (this.$store.state.customer && this.$store.state.customer.settings) || [];
      const tz = (rows.find(r => r && r.Property === 'GENERAL_TIMEZONE') || {}).PropertyValue;
      return dateFormatFor(tz);
    },

    saving() {
      return !!(this.composer && this.composer.saving);
    },
    /** There is task text to add (the seeded patient chip alone is not a task). */
    canAdd() {
      return !!(this.composer && !this.composer.saving && hasTaskText(this.composer.draftHtml || ''));
    },

    // PatientTasksCard.patientTasks, verbatim: only tasks actually tagged with THIS patient.
    patientTasks() {
      const n = Number(this.patient && this.patient.PatNum);
      const all = (this.$store.state.tasks && this.$store.state.tasks.tasksTaggedWithPatient) || [];
      return all.filter(t => Array.isArray(t.taggedPatients) && t.taggedPatients.includes(n));
    },
    // …then PatientTasksCard.openTasks: not completed, soonest due first, undated last.
    openTasks() {
      return this.patientTasks
        .filter(t => t.status !== 'COMPLETED')
        .slice()
        .sort((a, b) => {
          const da = moment(a.dueAt, DUE_FORMAT);
          const db = moment(b.dueAt, DUE_FORMAT);
          const va = da.isValid();
          const vb = db.isValid();
          if (!va && !vb) return 0;
          if (!va) return 1;
          if (!vb) return -1;
          return da.valueOf() - db.valueOf();
        });
    },
  },

  mounted() {
    this.composer = this.$refs.composer || null;
  },

  methods: {
    close() {
      this.$emit('closePanel', { action: 'cancel' });
    },

    onSaved(taskId) {
      if (this.$toast) this.$toast.success('Task created');
      this.$emit('closePanel', { action: 'saved', taskId });
    },

    add() {
      if (this.canAdd && this.composer) this.composer.saveTodo();
    },

    previewText(t) {
      const s = stripMentions(t.content);
      return s.length > 140 ? `${s.slice(0, 140)}…` : s;
    },

    assigneeNames(t) {
      const ids = Array.isArray(t.assignees) && t.assignees.length
        ? t.assignees
        : (t.assignedToEmpID ? [t.assignedToEmpID] : []);
      const names = ids
        .map((id) => {
          const emp = this.employees.find(e => e.EmployeeID === id);
          return emp ? emp.Name : null;
        })
        .filter(Boolean);
      return names.length ? names.join(', ') : '—';
    },

    formatDue(t) {
      const d = moment(t.dueAt, DUE_FORMAT);
      return d.isValid() ? d.format(this.officeDateFormat) : (t.dueAtStr || '—');
    },
  },
};
</script>

<style lang="scss" scoped>
@import './send-panel';

@include send-panel-base;

.nt-foot-btn { @include ap-btn-primary; }

.nt-hint { margin: 0; }
.nt-count { margin: 0; }

// The inline editor is a contenteditable <div>, which iOS Safari zooms into below 16px just like an
// <input>. On touch it matches the other panels' text fields.
.nt-compose ::v-deep .task-composer--inline .newtodo,
.nt-compose ::v-deep .task-composer--inline .newtodo[placeholder]:empty:before {
  @include ap-touch { font-size: 16px; }
}

.nt-open {
  margin: 0 -10px;
  padding: 0;
  list-style: none;
}

.nt-open__row {
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr);
  gap: 10px;
  padding: 10px;
  border-radius: 10px;

  &:hover { background: $pp-bg-2; }
}

.nt-open__ring {
  width: 20px;
  height: 20px;
  margin-top: 1px;
  border: 1.5px solid $neutral-300;
  border-radius: 50%;
}

.nt-open__body { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.nt-open__text { font-size: 14px; font-weight: 500; line-height: 1.35; overflow-wrap: anywhere; }

.nt-open__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  font-size: 12.5px;
  color: $pp-ink-3;

  i { margin-right: 4px; }
}
</style>
